import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, fileUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { resolveStepIcon } from "../lib/stepIcons";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

const SEEN_KEY = "hyppe_seen_onboarding_version";
// Routes where the popup must never AUTO-open (manual open still works everywhere).
const BLOCKED = ["/admin", "/cart", "/checkout", "/order-confirmed"];

// Manually open the (already-mounted) onboarding popup from anywhere — Header,
// Footer, etc. Reuses the single OnboardingPopup instance; no duplication.
export const openOnboarding = () => {
  window.dispatchEvent(new Event("hyppe:open-onboarding"));
};

export const OnboardingPopup = () => {
  const settings = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const cfg = settings.onboarding || {};

  const [steps, setSteps] = useState([]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);

  useEffect(() => {
    let alive = true;
    api.get("/onboarding-steps")
      .then((r) => { if (alive) setSteps(r.data.onboarding_steps || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const blocked = BLOCKED.some((p) => location.pathname.startsWith(p));

  // Manual open (Header / Footer "How It Works") — always works, ignores the
  // seen-version + enabled + route guards; starts from Step 1.
  useEffect(() => {
    const onOpen = () => { setDir(1); setIdx(0); setOpen(true); };
    window.addEventListener("hyppe:open-onboarding", onOpen);
    return () => window.removeEventListener("hyppe:open-onboarding", onOpen);
  }, []);

  useEffect(() => {
    if (blocked || open) return;
    if (!cfg.enabled || steps.length === 0) return;
    const seen = parseInt(localStorage.getItem(SEEN_KEY) || "0", 10);
    const version = Number(cfg.version) || 1;
    if (version > seen) {
      const t = setTimeout(() => setOpen(true), 650); // gentle delay after load
      return () => clearTimeout(t);
    }
  }, [cfg.enabled, cfg.version, steps.length, blocked, open]);

  const markSeen = useCallback(() => {
    localStorage.setItem(SEEN_KEY, String(Number(cfg.version) || 1));
  }, [cfg.version]);

  const close = useCallback(() => {
    markSeen();
    setOpen(false);
  }, [markSeen]);

  const finish = useCallback(() => {
    markSeen();
    setOpen(false);
    navigate("/shop");
  }, [markSeen, navigate]);

  const goNext = () => {
    if (idx >= steps.length - 1) { finish(); return; }
    setDir(1); setIdx((i) => i + 1);
  };
  const goBack = () => { if (idx > 0) { setDir(-1); setIdx((i) => i - 1); } };

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goBack();
    };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, steps.length]);

  if (!open || steps.length === 0) return null;

  const step = steps[Math.min(idx, steps.length - 1)];
  const Icon = resolveStepIcon(step.icon);
  const total = steps.length;
  const isLast = idx === total - 1;
  const heroImg = step.image_url || cfg.bg_image || "";

  return (
    <AnimatePresence>
      <motion.div
        data-testid="onboarding-popup"
        className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={close}
          data-testid="onboarding-backdrop"
        />

        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          className="relative w-full max-w-4xl bg-[#0A0A0A] border border-[#C5A059]/35 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] overflow-hidden grid md:grid-cols-2"
        >
          {/* Close */}
          <button
            data-testid="onboarding-close"
            onClick={close}
            aria-label="Close"
            className="absolute top-3 right-3 z-20 w-10 h-10 flex items-center justify-center text-[#F4EFE6]/70 hover:text-[#C5A059] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Image pane */}
          <div className="relative h-52 md:h-auto md:min-h-[460px] overflow-hidden bg-[#14100E]">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.img
                key={step.id || idx}
                src={fileUrl(heroImg)}
                alt={step.alt || step.title}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.55, ease: "easeOut" }}
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/45 to-transparent" />
            <div className="absolute top-4 left-5 md:top-6 md:left-7">
              <p className="font-mono-label text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">HYPPE AROMA</p>
            </div>
            <div className="absolute bottom-4 left-5 md:bottom-7 md:left-7">
              <span data-testid="onboarding-step-counter" className="font-serif-display text-4xl md:text-5xl text-[#F4EFE6]/90">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span className="font-mono-label text-xs text-[#F4EFE6]/40"> / {String(total).padStart(2, "0")}</span>
            </div>
          </div>

          {/* Content pane */}
          <div className="relative flex flex-col p-6 sm:p-9 md:p-10">
            <p className="eyebrow mb-5">{idx === 0 ? (cfg.eyebrow || "◐ First Time Here?") : (step.subtitle || "◐ How It Works")}</p>

            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={step.id || idx}
                custom={dir}
                initial={{ opacity: 0, x: dir > 0 ? 24 : -24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir > 0 ? -24 : 24 }}
                transition={{ duration: 0.32, ease: "easeOut" }}
                className="flex-1"
              >
                <div className="w-14 h-14 rounded-full border border-[#C5A059]/45 flex items-center justify-center text-[#C5A059] mb-6">
                  <Icon className="w-6 h-6" strokeWidth={1.4} />
                </div>
                {idx === 0 && (
                  <p className="font-mono-label text-[10px] uppercase tracking-[0.28em] text-[#F4EFE6]/45 mb-2">
                    {cfg.title || "How HYPPE AROMA Works"}
                  </p>
                )}
                <h2 data-testid="onboarding-step-title" className="font-serif-display text-3xl sm:text-4xl text-[#F4EFE6] leading-tight">
                  {step.title}
                </h2>
                <p className="mt-4 text-sm sm:text-base text-[#F4EFE6]/65 leading-relaxed max-w-md">
                  {step.description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Dots */}
            <div className="flex items-center gap-2 mt-8 mb-6">
              {steps.map((s, i) => (
                <button
                  key={s.id || i}
                  data-testid={`onboarding-dot-${i}`}
                  onClick={() => { setDir(i > idx ? 1 : -1); setIdx(i); }}
                  aria-label={`Go to step ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-[#C5A059]" : "w-1.5 bg-[#F4EFE6]/25 hover:bg-[#F4EFE6]/50"}`}
                />
              ))}
            </div>

            {/* Nav */}
            <div className="flex items-center justify-between gap-4 border-t border-white/10 pt-6">
              <div className="flex items-center gap-3">
                {idx > 0 ? (
                  <button data-testid="onboarding-back" onClick={goBack}
                    className="inline-flex items-center gap-2 font-mono-label text-[11px] uppercase tracking-[0.2em] text-[#F4EFE6]/60 hover:text-[#C5A059] transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                ) : (
                  <button data-testid="onboarding-skip" onClick={close}
                    className="font-mono-label text-[11px] uppercase tracking-[0.2em] text-[#F4EFE6]/45 hover:text-[#F4EFE6]/80 transition-colors">
                    Skip
                  </button>
                )}
              </div>

              {isLast ? (
                <button data-testid="onboarding-cta" onClick={finish} className="btn-gold hover:-translate-y-0.5">
                  {cfg.cta_label || "Start Exploring"} <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button data-testid="onboarding-next" onClick={goNext} className="btn-gold hover:-translate-y-0.5">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingPopup;
